import { useEffect } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import LevelIndicator from "../components/LevelIndicator";
import TierBadge from "../components/TierBadge";
import { Crumbs, LetterBar } from "../components/CourseBits";
import { useCoursesData } from "../lib/courseData";
import { LETTERS, firstLetter, sortAlpha } from "../lib/courseMeta";
/** Glossaire : toutes les définitions d'une lettre, avec navigation vers les lettres voisines. */
export default function GlossaryLetterPage() {
  const { letter = "a" } = useParams();
  const L = letter.toUpperCase();
  const { courses, isLoading } = useCoursesData();
  useEffect(() => { window.scrollTo(0, 0); }, [L]);
  const terms = courses.filter((c) => c.category === "vocabulary");
  const available = new Set(terms.map((t) => firstLetter(t.title)));
  const list = sortAlpha(terms.filter((t) => firstLetter(t.title) === L));
  const letters = LETTERS.filter((x) => available.has(x));
  const i = letters.indexOf(L);
  const prev = letters[i - 1], next = letters[i + 1];
  if (!LETTERS.includes(L)) return <Navigate to="/cours" replace />;
  return (
    <div className="min-h-screen" data-testid="glossary-letter-page">
      <section className="border-b border-white/5">
        <div className="mx-auto max-w-4xl px-6 pt-8 pb-8">
          <Crumbs items={[["Cours", "/cours"], ["Glossaire", "/cours"], [`Lettre ${L}`]]} />
          <div className="mt-6 text-xs uppercase tracking-[0.2em] text-orange-400 mb-3">Glossaire</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl">Lettre <span className="text-orange-400">{L}</span></h1>
          {!isLoading && <p className="mt-3 text-zinc-400">{list.length} définition{list.length > 1 ? "s" : ""}</p>}
        </div>
      </section>
      <LetterBar active={L} available={available} />
      <section className="mx-auto max-w-4xl px-6 py-8">
        {isLoading ? (
          <div className="h-64 rounded-xl bg-white/[0.02] border border-white/5 animate-pulse" />
        ) : list.length === 0 ? (
          <div className="rounded-xl border border-dashed border-white/10 py-12 text-center text-sm text-zinc-500">Aucune définition pour cette lettre.</div>
        ) : (
          <div className="rounded-xl border border-white/5 bg-card divide-y divide-white/5">
            {list.map((t) => (
              <div key={t.id} className="px-5 py-5" data-testid={`glossary-term-${t.id}`}>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <h2 className="font-semibold text-lg text-zinc-100">{t.title}</h2>
                  <LevelIndicator level={t.level} />
                  {t.is_premium && <TierBadge tier="essentiel" locked={!!t.is_locked} />}
                </div>
                <p className="mt-1.5 text-[15px] text-zinc-400 leading-relaxed">{t.description}</p>
                <Link to={`/cours/${t.slug}`} className="mt-2 inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-orange-300">
                  Lire la fiche complète <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            ))}
          </div>
        )}
        <div className="mt-6 grid grid-cols-2 gap-3">
          <div>
            {prev && (
              <Link to={`/cours/glossaire/${prev.toLowerCase()}`} className="block rounded-xl border border-white/5 bg-card hover:border-orange-500/30 p-4">
                <div className="text-xs text-zinc-500 inline-flex items-center gap-1"><ArrowLeft className="w-3 h-3" /> Lettre précédente</div>
                <div className="mt-1 font-display font-black text-2xl text-orange-300">{prev}</div>
              </Link>
            )}
          </div>
          <div>
            {next && (
              <Link to={`/cours/glossaire/${next.toLowerCase()}`} className="block text-right rounded-xl border border-white/5 bg-card hover:border-orange-500/30 p-4">
                <div className="text-xs text-zinc-500 inline-flex items-center gap-1">Lettre suivante <ArrowRight className="w-3 h-3" /></div>
                <div className="mt-1 font-display font-black text-2xl text-orange-300">{next}</div>
              </Link>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
